/**
 * Inyecta el header `X-Tenant` cuando estamos en un subdominio
 * que matchea un tenant. En dev es redundante con el `Host` que ve
 * el backend, pero en producción es la ÚNICA señal de tenant: el
 * front llama al backend por su propia URL (VITE_API_URL), así que
 * el `Host` que ve el backend es el suyo, no el del navegador.
 *
 * Detección del slug, en orden de prioridad:
 *   1. Subdominio del hostname actual.
 *   2. `localStorage.tenantSlug` (set por el AuthContext al logear).
 *   3. `null` (modo plataforma, sin header X-Tenant).
 *
 * Configura los dominios raíz de producción con la env de build
 * `VITE_TENANT_ROOT_DOMAINS` (CSV), ej: "tnlog.com.co,optycargo.com".
 */
const DEFAULT_ROOT_DOMAINS = ['localhost', '127.0.0.1'];

// Subdominios que nunca son tenants (plataforma, API, etc.).
const RESERVED_SUBDOMAINS = ['www', 'app', 'api', 'admin', 'plataforma'];

const LS_TENANT_KEY = 'tenantSlug';

function tenantRootDomains() {
  const fromEnv =
    typeof import.meta !== 'undefined' ? import.meta.env?.VITE_TENANT_ROOT_DOMAINS : '';
  const extra = String(fromEnv || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return [...DEFAULT_ROOT_DOMAINS, ...extra];
}

function detectTenantSlugFromHost() {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.split(':')[0].toLowerCase();
  for (const root of tenantRootDomains()) {
    if (host === root) return null; // dominio raíz → plataforma
    if (host.endsWith(`.${root}`)) {
      const sub = host.slice(0, host.length - root.length - 1);
      if (!sub || RESERVED_SUBDOMAINS.includes(sub) || sub.includes('.')) return null;
      return sub;
    }
  }
  // Hostname que no cuelga de ningún dominio raíz conocido
  // (ej. *.up.railway.app) → modo plataforma.
  return null;
}

function detectTenantSlugFromStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(LS_TENANT_KEY);
    if (!v) return null;
    const slug = String(v).trim().toLowerCase();
    if (!slug || RESERVED_SUBDOMAINS.includes(slug)) return null;
    return slug;
  } catch {
    return null;
  }
}

export function getTenantSlug() {
  // Prioridad: subdominio > localStorage.
  // El subdominio es la fuente de verdad en producción, pero en
  // dev (localhost sin subdominio) caemos al slug del localStorage
  // para que el header X-Tenant se siga mandando.
  return detectTenantSlugFromHost() || detectTenantSlugFromStorage();
}

/** Setea el tenant actual (lo llama el AuthContext al logear). */
export function setTenantSlug(slug) {
  if (typeof window === 'undefined') return;
  try {
    if (slug && String(slug).trim()) {
      window.localStorage.setItem(LS_TENANT_KEY, String(slug).trim().toLowerCase());
    } else {
      window.localStorage.removeItem(LS_TENANT_KEY);
    }
  } catch {
    /* noop */
  }
}

export function getAuthToken() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('token');
}

export function tenantHeaders(extra = {}) {
  const slug = getTenantSlug();
  const token = getAuthToken();
  const headers = { ...extra };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (!slug) return headers;
  return { ...headers, 'X-Tenant': slug };
}

export function withTenant(headers = {}) {
  return tenantHeaders(headers);
}
